import { IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import { VisibilityOffOutlined } from '@mui/icons-material';
import { useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import RoiColor from '~/components/Structures/RoiColor';
import StructureColorButton from '~/components/Structures/StructureColorButton';
import { RoiId } from '~/domain/changes/ids';
import { isApprovalStatusEditable } from '~/domain/dicom/dicomConstants';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import useDoseTargetLookup from '~/hooks/patient/dose/useDoseTargetLookup';
import useRoi from '~/hooks/structure-set/useRoi';
import useRoiDeselect from '~/hooks/structure-set/useRoiDeselect';
import useRoiRename from '~/hooks/structure-set/useRoiRename';
import useRoiSelect from '~/hooks/structure-set/useRoiSelect';
import {
    COLOR_EDIT_OPENER_ATTRIBUTE,
    structureRowAnchor,
    useStructureColorOpener,
} from '~/hooks/structure-set/useStructureColorEdit';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import { LEADING_COLUMN_PX } from '../leadingColumn';
import StructureField from './StructureField';
import { ROW_ACTIONS_CLASS } from './StructureField/Actions';

/** How long a click on the dot waits for a second one to make it a double-click. */
const DOUBLE_CLICK_MS = 200;

export default function StructureProperties({
    roi,
    roiId,
    onSelectionInput,
}: {
    roi: RoiReadModel;
    /** Branded RoiId from the projection layer. When present, rename is
     * dispatched through useRoiCommands (new path). Falls back to the
     * legacy useRoi domain-event path when absent. */
    roiId?: RoiId;
    onSelectionInput?: (
        roiNumber: number,
        mods: { ctrl: boolean; shift: boolean }
    ) => boolean;
}) {
    const intl = useIntl();
    const fieldAnchorRef = useRef<HTMLDivElement | null>(null);
    const { setVisible, isRoiEmpty } = useRoi(roi);
    const setName = useRoiRename(roi, roiId);
    const {
        color: displayColor,
        withheldReasonId: colorEditWithheldReasonId,
        toggle: toggleColorEdit,
        openerProps,
    } = useStructureColorOpener(roi, 'row');
    const colorEditOpen = openerProps['aria-expanded'];
    const colorCanBeEdited = colorEditWithheldReasonId === undefined;
    const dotClickRef = useRef<ReturnType<typeof setTimeout>>();
    const selectedROI = useRecoilValue<number>(selectedROIState);
    const select = useRoiSelect();
    const deselect = useRoiDeselect(roi);
    const pendingClickRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const ROINumber = roi.roiNumber;
    const isSelected = selectedROI === ROINumber;
    const isTarget = useDoseTargetLookup().has(roi.name);
    const dot = {
        isEmptyVolume: isRoiEmpty(),
        isMarker: roi.isPointRoi,
        isTarget,
    };

    const structureCanBeEdited = isApprovalStatusEditable(roi.approvalStatus);

    const handleSelectROI = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
            const mods = {
                ctrl: e.ctrlKey || e.metaKey,
                shift: e.shiftKey,
            };
            if (onSelectionInput?.(roi.roiNumber, mods)) {
                return;
            }
            if (e.detail > 1) {
                if (pendingClickRef.current) {
                    clearTimeout(pendingClickRef.current);
                    pendingClickRef.current = null;
                }
                return;
            }
            if (isSelected) {
                pendingClickRef.current = setTimeout(() => {
                    pendingClickRef.current = null;
                    deselect();
                }, 200);
                return;
            }
            select(roi);
        },
        [isSelected, deselect, select, roi, onSelectionInput]
    );

    const handleMakeVisible = (e: React.MouseEvent) => {
        e.stopPropagation();
        setVisible(true);
    };

    // On a row that is not selected, a click on the dot hides the
    // structure, and a double-click or a right-click opens its colour edit.
    // The click waits out the double-click interval, so a double-click
    // never also hides the structure. While that edit is open a click or a
    // right-click closes it instead, and a double-click is that one click:
    // it does not open the edit afresh. Selecting the row makes the dot the
    // colour button. A hidden structure shows an eye in place of its dot,
    // which shows it again.
    const toggleColorEditAtRow = (e: React.MouseEvent<HTMLElement>) =>
        toggleColorEdit(
            structureRowAnchor(roi.roiNumber, fieldAnchorRef.current) ??
                e.currentTarget
        );
    /** Drops the waiting show/hide click; true when one was waiting. */
    const dropWaitingDotClick = () => {
        const wasWaiting = dotClickRef.current !== undefined;
        clearTimeout(dotClickRef.current);
        dotClickRef.current = undefined;
        return wasWaiting;
    };
    const handleDotClick = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        // The later clicks of a double-click are the double-click's.
        if (e.detail > 1) {
            return;
        }
        dropWaitingDotClick();
        if (isSelected) {
            return;
        }
        if (colorEditOpen) {
            toggleColorEditAtRow(e);
        } else {
            dotClickRef.current = setTimeout(() => {
                dotClickRef.current = undefined;
                setVisible(!roi.visible);
            }, DOUBLE_CLICK_MS);
        }
    };
    const handleDotDoubleClick = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        // Opens only in place of a show/hide still waiting: a first click
        // that closed the edit, or already showed or hid the structure, has
        // been answered.
        if (dropWaitingDotClick() && colorCanBeEdited && !colorEditOpen) {
            toggleColorEditAtRow(e);
        }
    };
    const handleDotContextMenu = (e: React.MouseEvent<HTMLElement>) => {
        // The dot's own, whether or not it opens anything: never the
        // browser's menu.
        e.preventDefault();
        e.stopPropagation();
        if (colorCanBeEdited) {
            toggleColorEditAtRow(e);
        }
    };

    return (
        <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
                cursor: 'pointer',
                height: 28,
                // The selected row's actions, on any row pointed at, and on
                // one holding the keyboard focus so they stay reachable.
                [`&:hover .${ROW_ACTIONS_CLASS}, &:focus-within .${ROW_ACTIONS_CLASS}`]:
                    { display: 'flex' },
            }}
            width="100%"
            onClick={handleSelectROI}
            onMouseDown={(e) => {
                // Suppress native text selection when shift-extending a range;
                // click-time preventDefault is too late.
                if (e.shiftKey) {
                    e.preventDefault();
                }
            }}
            data-testid="structure-properties-field"
            ref={fieldAnchorRef}
        >
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                width={LEADING_COLUMN_PX}
                height={20}
            >
                {!roi.visible ? (
                    <IconButton
                        onClick={handleMakeVisible}
                        aria-label={intl.formatMessage({
                            id: 'showStructure',
                        })}
                        sx={{
                            width: LEADING_COLUMN_PX,
                            height: 20,
                            p: 0,
                            borderRadius: 1,
                            color: 'text.secondary',
                        }}
                    >
                        <VisibilityOffOutlined sx={{ fontSize: 14 }} />
                    </IconButton>
                ) : isSelected && colorCanBeEdited ? (
                    <StructureColorButton
                        roi={roi}
                        source="row"
                        anchorFallbackRef={fieldAnchorRef}
                        size={12}
                        {...dot}
                    />
                ) : (
                    // A selected row's dot swallows its clicks but offers no
                    // action, so it is no button. Elsewhere it hides the
                    // structure, or closes the colour edit while that is open.
                    <Box
                        component={isSelected ? 'span' : 'button'}
                        onClick={handleDotClick}
                        onDoubleClick={handleDotDoubleClick}
                        onContextMenu={handleDotContextMenu}
                        {...(isSelected
                            ? colorEditOpen && {
                                  [COLOR_EDIT_OPENER_ATTRIBUTE]: '',
                              }
                            : colorEditOpen
                              ? openerProps
                              : {
                                    type: 'button',
                                    'aria-label': intl.formatMessage({
                                        id: 'hideStructure',
                                    }),
                                })}
                        data-testid={`structure-color-dot-${roi.roiNumber}`}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: LEADING_COLUMN_PX,
                            height: 20,
                            p: 0,
                            border: 0,
                            borderRadius: 1,
                            backgroundColor: 'transparent',
                            cursor: isSelected ? 'default' : 'pointer',
                            '&:hover': {
                                backgroundColor: isSelected
                                    ? 'transparent'
                                    : 'action.hover',
                            },
                        }}
                    >
                        <RoiColor color={displayColor} size={12} {...dot} />
                    </Box>
                )}
            </Box>
            <Box flex={1} minWidth={0}>
                <StructureField
                    roi={roi}
                    onNameChange={setName}
                    isEditable={structureCanBeEdited}
                    isSelected={isSelected}
                    disabled={!roi.visible && !isSelected}
                    approvalStatus={roi.approvalStatus}
                />
            </Box>
        </Box>
    );
}
